import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import EnhancedRegistration from './EnhancedRegistration';
import ForgotPassword from './ForgotPassword';

// Same word-cycle as the landing page Hero — keep text/timing/colors in sync.
const HERO_WORDS = ["Automated", "Supercharged", "Transformed", "Accelerated"];

export default function Login() {
  const [currentView, setCurrentView] = useState('login');
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [wordIdx, setWordIdx] = useState(0);
  const [wordExiting, setWordExiting] = useState(false);

  const { login } = useAuth();

  useEffect(() => {
    const id = setInterval(() => {
      setWordExiting(true);
      setTimeout(() => {
        setWordIdx(i => (i + 1) % HERO_WORDS.length);
        setWordExiting(false);
      }, 240);
    }, 2800);
    return () => clearInterval(id);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await login(formData.username, formData.password);
      if (!result.success) setError(result.message);
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (currentView === 'register') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #ffffff 0%, #eafaf4 55%, #e3f6ee 100%)', display: 'flex', position: 'relative', overflow: 'hidden' }}>
        {/* Glow orbs — same as login */}
        <div style={{ position: 'absolute', width: 700, height: 700, borderRadius: '50%', background: 'rgba(124,58,237,0.14)', filter: 'blur(100px)', top: -200, left: -200, pointerEvents: 'none', zIndex: 0 }} />
        <div style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%', background: 'rgba(59,130,246,0.10)', filter: 'blur(80px)', bottom: -100, right: -100, pointerEvents: 'none', zIndex: 0 }} />
        <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'rgba(139,92,246,0.10)', filter: 'blur(60px)', top: '50%', left: '40%', pointerEvents: 'none', zIndex: 0 }} />
        {/* Grid overlay */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.03, zIndex: 0, backgroundImage: 'linear-gradient(rgba(15,23,42,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.7) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        {/* Full-width scrollable form area */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '48px 24px 48px', overflowY: 'auto', position: 'relative', zIndex: 10 }}>
          <div style={{ width: '100%', maxWidth: 520 }}>
            <EnhancedRegistration onSwitchToLogin={() => setCurrentView('login')} />
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'forgot') {
    return <ForgotPassword onBackToLogin={() => setCurrentView('login')} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #ffffff 0%, #eafaf4 55%, #e3f6ee 100%)', display: 'flex', position: 'relative', overflow: 'hidden' }}>

      {/* Glow orbs */}
      <div style={{ position: 'absolute', width: 700, height: 700, borderRadius: '50%', background: 'rgba(124,58,237,0.14)', filter: 'blur(100px)', top: -200, left: -200, pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%', background: 'rgba(59,130,246,0.10)', filter: 'blur(80px)', bottom: -100, right: -100, pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'rgba(139,92,246,0.10)', filter: 'blur(60px)', top: '50%', left: '40%', pointerEvents: 'none', zIndex: 0 }} />

      {/* Grid overlay */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.03, zIndex: 0,
        backgroundImage: 'linear-gradient(rgba(15,23,42,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.7) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />

      {/* Left side — branding + workflow canvas */}
      <div className="login-left-panel" style={{ display: 'none', flex: 1, position: 'relative', zIndex: 10, flexDirection: 'column', justifyContent: 'center', padding: '48px 52px' }}>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 36 }}>
          <img src="/cognixaihub_new_logo.png" alt="CognixAI Hub" style={{ width: 192, height: 64, objectFit: 'contain' }} />
        </div>

        <h1 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.15, marginBottom: 12 }}>
          Your Business,<br />
          <span
            key={wordIdx}
            className={wordExiting ? 'hero-word-exit' : 'hero-word-enter'}
            style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #0891b2 0%, #0d9488 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}
          >
            {HERO_WORDS[wordIdx]}
          </span>
        </h1>
        <p style={{ color: 'rgba(15,23,42,0.55)', fontSize: '1rem', lineHeight: 1.7, marginBottom: 32, maxWidth: 420 }}>
          Deploy AI agents that connect your data, automate workflows, and deliver insights — so your team focuses on what matters.
        </p>

        {/* Workflow Canvas Mockup */}
        <div style={{
          background: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(15,23,42,0.08)',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(15,23,42,0.10)',
        }}>
          {/* Canvas header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid rgba(15,23,42,0.07)' }}>
            <span style={{ color: 'rgba(15,23,42,0.45)', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Workflow Canvas</span>
            <span style={{ fontSize: '0.7rem', padding: '2px 10px', borderRadius: 999, background: 'rgba(16,185,129,0.12)', color: '#059669', border: '1px solid rgba(16,185,129,0.3)' }}>● Running</span>
          </div>

          {/* Canvas area */}
          <div style={{ padding: '20px 18px 16px', position: 'relative', height: 250 }}>

            {/* Dotted background */}
            <div style={{
              position: 'absolute', inset: 0, opacity: 0.06,
              backgroundImage: 'radial-gradient(circle, rgba(15,23,42,0.8) 1px, transparent 1px)',
              backgroundSize: '18px 18px',
            }} />

            {/* SVG edges */}
            <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0 }}>
              <path d="M 115 72 C 155 72 155 95 195 95" stroke="rgba(124,58,237,0.55)" strokeWidth="1.5" fill="none" strokeDasharray="4 3" />
              <path d="M 305 95 C 345 95 345 58 375 58" stroke="rgba(79,70,229,0.55)" strokeWidth="1.5" fill="none" strokeDasharray="4 3" />
              <path d="M 305 95 C 345 95 345 152 375 152" stroke="rgba(79,70,229,0.55)" strokeWidth="1.5" fill="none" strokeDasharray="4 3" />
              <path d="M 470 58 C 505 58 505 110 505 110" stroke="rgba(5,150,105,0.55)" strokeWidth="1.5" fill="none" strokeDasharray="4 3" />
              <path d="M 470 152 C 505 152 505 110 505 110" stroke="rgba(5,150,105,0.55)" strokeWidth="1.5" fill="none" strokeDasharray="4 3" />
            </svg>

            {/* Node: Webhook */}
            <div style={{ position: 'absolute', left: 16, top: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ borderRadius: 14, padding: '8px 12px', textAlign: 'center', border: '1px solid rgba(124,58,237,0.3)', background: 'rgba(124,58,237,0.08)', width: 90 }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                </div>
                <div style={{ color: '#7c3aed', fontSize: '0.65rem', fontWeight: 700 }}>Webhook</div>
                <div style={{ color: 'rgba(15,23,42,0.45)', fontSize: '0.6rem' }}>Trigger</div>
              </div>
              <div style={{ width: 2, height: 14, background: 'linear-gradient(to bottom, rgba(124,58,237,0.4), transparent)' }} />
            </div>

            {/* Node: LLM (active, pulsing) */}
            <div style={{ position: 'absolute', left: 190, top: 68, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ position: 'relative', borderRadius: 14, padding: '8px 12px', textAlign: 'center', border: '1px solid rgba(79,70,229,0.35)', background: 'rgba(79,70,229,0.10)', width: 96, boxShadow: '0 0 20px rgba(79,70,229,0.12)' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a5 5 0 0 1 5 5c0 1.5-.6 2.8-1.6 3.8L17 13h-2l-1-1.5A5 5 0 0 1 7 7a5 5 0 0 1 5-5z"/>
                    <path d="M9 17v4m6-4v4M9 17h6"/>
                    <circle cx="9" cy="9" r="1" fill="#4f46e5"/><circle cx="15" cy="9" r="1" fill="#4f46e5"/>
                  </svg>
                </div>
                <div style={{ color: '#4f46e5', fontSize: '0.65rem', fontWeight: 700 }}>LLM Node</div>
                <div style={{ color: 'rgba(15,23,42,0.45)', fontSize: '0.6rem' }}>claude-sonnet</div>
                <div style={{ position: 'absolute', inset: -4, borderRadius: 18, border: '1px solid rgba(79,70,229,0.25)', animation: 'pulse 2s ease-in-out infinite' }} />
              </div>
            </div>

            {/* Node: Query */}
            <div style={{ position: 'absolute', left: 368, top: 30 }}>
              <div style={{ borderRadius: 14, padding: '8px 12px', textAlign: 'center', border: '1px solid rgba(37,99,235,0.3)', background: 'rgba(37,99,235,0.08)', width: 92 }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <ellipse cx="12" cy="5" rx="9" ry="3"/>
                    <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>
                    <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/>
                  </svg>
                </div>
                <div style={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 700 }}>Query Node</div>
                <div style={{ color: 'rgba(15,23,42,0.45)', fontSize: '0.6rem' }}>PostgreSQL</div>
              </div>
            </div>

            {/* Node: Transform */}
            <div style={{ position: 'absolute', left: 368, top: 128 }}>
              <div style={{ borderRadius: 14, padding: '8px 12px', textAlign: 'center', border: '1px solid rgba(217,119,6,0.3)', background: 'rgba(217,119,6,0.08)', width: 92 }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                </div>
                <div style={{ color: '#d97706', fontSize: '0.65rem', fontWeight: 700 }}>Transform</div>
                <div style={{ color: 'rgba(15,23,42,0.45)', fontSize: '0.6rem' }}>JSON → CSV</div>
              </div>
            </div>

            {/* Node: End */}
            <div style={{ position: 'absolute', left: 492, top: 90 }}>
              <div style={{ borderRadius: 14, padding: '8px 10px', textAlign: 'center', border: '1px solid rgba(5,150,105,0.3)', background: 'rgba(5,150,105,0.08)', width: 64 }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div style={{ color: '#059669', fontSize: '0.65rem', fontWeight: 700 }}>End</div>
              </div>
            </div>

            {/* Node palette */}
            <div style={{ position: 'absolute', bottom: 12, left: 16, display: 'flex', gap: 6 }}>
              {['API', 'Code', 'Email', 'Sheets'].map(n => (
                <span key={n} style={{ fontSize: '0.6rem', color: 'rgba(15,23,42,0.4)', padding: '2px 7px', borderRadius: 6, border: '1px solid rgba(15,23,42,0.1)', background: 'rgba(15,23,42,0.02)' }}>{n}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right side — login form */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 24px', position: 'relative', zIndex: 10 }}>
        <div style={{
          width: '100%',
          maxWidth: 400,
          background: 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(15,23,42,0.08)',
          borderRadius: 20,
          padding: '36px 32px',
          boxShadow: '0 32px 80px rgba(15,23,42,0.12), 0 0 0 1px rgba(124,58,237,0.06)',
        }}>

          <h2 style={{ color: '#0f172a', fontSize: '1.5rem', fontWeight: 700, textAlign: 'center', marginBottom: 6 }}>Welcome back</h2>
          <p style={{ color: 'rgba(15,23,42,0.5)', fontSize: '0.85rem', textAlign: 'center', marginBottom: 28 }}>Sign in to your account</p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div>
              <label style={{ display: 'block', color: 'rgba(15,23,42,0.65)', fontSize: '0.8rem', fontWeight: 500, marginBottom: 6 }}>Username</label>
              <input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                required
                placeholder="Enter your username"
                style={{
                  width: '100%', padding: '11px 14px',
                  background: '#f8fafc',
                  border: '1px solid rgba(15,23,42,0.14)',
                  borderRadius: 10, color: '#0f172a', fontSize: '0.9rem',
                  outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(124,58,237,0.6)'}
                onBlur={e => e.target.style.borderColor = 'rgba(15,23,42,0.14)'}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: 'rgba(15,23,42,0.65)', fontSize: '0.8rem', fontWeight: 500, marginBottom: 6 }}>Password</label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter your password"
                style={{
                  width: '100%', padding: '11px 14px',
                  background: '#f8fafc',
                  border: '1px solid rgba(15,23,42,0.14)',
                  borderRadius: 10, color: '#0f172a', fontSize: '0.9rem',
                  outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(124,58,237,0.6)'}
                onBlur={e => e.target.style.borderColor = 'rgba(15,23,42,0.14)'}
              />
              <div style={{ textAlign: 'right', marginTop: 6 }}>
                <button
                  type="button"
                  onClick={() => { setCurrentView('forgot'); setError(''); setFormData({ username: '', password: '' }); }}
                  style={{ background: 'none', border: 'none', color: '#7c3aed', fontSize: '0.78rem', cursor: 'pointer', padding: 0 }}
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            {error && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: '0.82rem' }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%', padding: '12px', borderRadius: 12, border: 'none',
                background: isLoading ? 'rgba(124,58,237,0.5)' : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                boxShadow: isLoading ? 'none' : '0 0 24px rgba(124,58,237,0.35)',
                color: '#fff', fontWeight: 700, fontSize: '0.95rem',
                cursor: isLoading ? 'not-allowed' : 'pointer', transition: 'all 0.2s', marginTop: 4,
              }}
              onMouseEnter={e => { if (!isLoading) e.target.style.boxShadow = '0 0 40px rgba(124,58,237,0.55)'; }}
              onMouseLeave={e => { if (!isLoading) e.target.style.boxShadow = '0 0 24px rgba(124,58,237,0.35)'; }}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                  Signing in...
                </span>
              ) : 'Sign In'}
            </button>
          </form>

          <p style={{ textAlign: 'center', color: 'rgba(15,23,42,0.5)', fontSize: '0.82rem', marginTop: 22 }}>
            Don't have an account?{' '}
            <button
              onClick={() => { setCurrentView('register'); setError(''); setFormData({ username: '', password: '' }); }}
              style={{ background: 'none', border: 'none', color: '#7c3aed', fontWeight: 600, cursor: 'pointer', padding: 0 }}
            >
              Register
            </button>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 0.6; transform: scale(1); } 50% { opacity: 0.2; transform: scale(1.05); } }
        @keyframes hero-word-exit { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-20px); } }
        @keyframes hero-word-enter { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .hero-word-exit  { animation: hero-word-exit 0.22s ease-out forwards; }
        .hero-word-enter { animation: hero-word-enter 0.22s ease-out forwards; }
        @media (min-width: 1024px) {
          .login-left-panel { display: flex !important; }
        }
        input::placeholder { color: rgba(15,23,42,0.35); }
      `}</style>
    </div>
  );
}
